import { supabase } from "@/lib/supabase";

/**
 * ImageProcessor — Sistema global y centralizado de procesamiento de imágenes
 * para VendeYa.
 *
 * REGLA PRINCIPAL: toda imagen subida se valida, se redimensiona, se convierte
 * a WebP y se optimiza ANTES de almacenarse. Ninguna imagen original (JPG/PNG/
 * GIF/HEIC) se usa directamente como recurso público.
 *
 * Pipeline: uploadImage → validateImage → resizeImage → convertToWebP →
 * compressImage → uploadToStorage → returnWebPUrl.
 */

export type ImageKind =
  | "product"
  | "profile"
  | "banner"
  | "category"
  | "thumbnail"
  | "document";

export type ProcessStatus = "processing" | "done" | "error";

export interface ProcessedImage {
  /** Ruta interna en Storage (sin URL). */
  path: string;
  /** URL pública del archivo principal. */
  url: string;
  thumbPath?: string;
  thumbUrl?: string;
  smallPath?: string;
  smallUrl?: string;
}

interface KindConfig {
  maxDim: number;
  quality: number;
  thumbnails: boolean;
}

/**
 * Configuración de calidad y dimensiones por tipo de imagen. La calidad
 * prioriza: calidad visual + bajo peso + carga rápida. Puede ajustarse aquí
 * (o desde configuración) sin tocar el resto del código.
 */
const KIND_CONFIG: Record<ImageKind, KindConfig> = {
  product: { maxDim: 2000, quality: 0.82, thumbnails: true },
  profile: { maxDim: 800, quality: 0.82, thumbnails: false },
  banner: { maxDim: 2400, quality: 0.82, thumbnails: false },
  category: { maxDim: 1200, quality: 0.82, thumbnails: true },
  thumbnail: { maxDim: 400, quality: 0.75, thumbnails: false },
  document: { maxDim: 1600, quality: 0.85, thumbnails: false },
};

/** Límite de entrada antes del procesamiento (evita cargas excesivas). */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

const THUMB_DIM = 400;
const SMALL_DIM = 800;
const THUMB_QUALITY = 0.75;
const SMALL_QUALITY = 0.8;

const ALLOWED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
]);

const ALLOWED_EXT = /\.(jpe?g|png|webp|gif|heic|heif)$/i;

export interface ImageValidation {
  ok: boolean;
  error?: string;
}

/**
 * Valida el archivo (MIME type, extensión, tamaño y no-vacío). No confía
 * únicamente en la extensión: exige MIME permitido O extensión permitida.
 */
export function validateImage(
  file: File,
  maxBytes: number = MAX_IMAGE_BYTES
): ImageValidation {
  const mime = (file.type || "").toLowerCase();
  const ext = file.name.toLowerCase().match(ALLOWED_EXT)?.[1] ?? null;
  const hasAllowedMime = ALLOWED_MIME.has(mime);
  const hasAllowedExt = !!ext;

  if (!hasAllowedMime && !hasAllowedExt) {
    return {
      ok: false,
      error: "Formato no permitido. Usa JPG, PNG, WEBP, GIF o HEIC.",
    };
  }
  if (file.size <= 0) {
    return { ok: false, error: "El archivo está vacío." };
  }
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: `La imagen supera el límite de ${Math.round(
        maxBytes / 1024 / 1024
      )} MB.`,
    };
  }
  return { ok: true };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("No se pudo leer la imagen. Usa un archivo válido."));
    img.src = url;
  });
}

/**
 * Dibuja la imagen en un canvas redimensionada (manteniendo el aspect-ratio)
 * y la codifica a WebP con la calidad indicada.
 */
function drawToWebP(
  img: HTMLImageElement,
  maxDim: number,
  quality: number
): Promise<Blob> {
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  let w = width;
  let h = height;
  if (Math.max(w, h) > maxDim) {
    const ratio = maxDim / Math.max(w, h);
    w = Math.max(1, Math.round(w * ratio));
    h = Math.max(1, Math.round(h * ratio));
  }
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo procesar la imagen.");
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new Error("No se pudo convertir la imagen a WebP.")),
      "image/webp",
      quality
    );
  });
}

/** Convierte un archivo a un blob WebP redimensionado y optimizado. */
export async function convertToWebP(
  file: File,
  opts: { maxDim: number; quality?: number }
): Promise<Blob> {
  const quality = opts.quality ?? 0.85;
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return await drawToWebP(img, opts.maxDim, quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function randomId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/**
 * Nombre base seguro y único (sin extensión). Nunca usa el nombre enviado por
 * el usuario directamente: elimina espacios y caracteres especiales y añade
 * un sufijo aleatorio para evitar duplicados.
 */
export function safeImageBase(prefix: string): string {
  const clean =
    (prefix || "imagen")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 30) || "imagen";
  return `${clean}_${randomId()}`;
}

async function uploadBlobToStorage(
  bucket: string,
  path: string,
  blob: Blob
): Promise<void> {
  const { error } = await supabase.storage.from(bucket).upload(path, blob, {
    contentType: "image/webp",
    cacheControl: "31536000",
    upsert: false,
  });
  if (error) throw new Error(`No se pudo subir la imagen (${error.message}).`);
}

/** Devuelve la URL pública de una ruta de almacenamiento. */
export function publicUrl(bucket: string, path: string): string {
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
}

export interface UploadImageOptions {
  file: File;
  bucket: string;
  folder: string;
  kind: ImageKind;
  prefix?: string;
  onProgress?: (status: ProcessStatus, message?: string) => void;
}

/**
 * Pipeline completo de subida: valida → redimensiona → convierte a WebP →
 * genera miniaturas (si aplica) → sube a Storage → devuelve rutas y URLs.
 * Único punto de entrada para subir imágenes en VendeYa.
 */
export async function processAndUploadImage(
  opts: UploadImageOptions
): Promise<ProcessedImage> {
  const { file, bucket, folder, kind, prefix = "imagen", onProgress } = opts;

  const validation = validateImage(file);
  if (!validation.ok) {
    throw new Error(validation.error ?? "Imagen inválida.");
  }

  const cfg = KIND_CONFIG[kind];
  onProgress?.("processing", "Procesando imagen…");

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);

    const base = safeImageBase(prefix);
    const mainPath = `${folder}/${base}.webp`;
    const mainBlob = await drawToWebP(img, cfg.maxDim, cfg.quality);
    await uploadBlobToStorage(bucket, mainPath, mainBlob);

    const result: ProcessedImage = {
      path: mainPath,
      url: publicUrl(bucket, mainPath),
    };

    if (cfg.thumbnails) {
      const thumbBlob = await drawToWebP(img, THUMB_DIM, THUMB_QUALITY);
      const thumbPath = `${folder}/${base}-thumb.webp`;
      await uploadBlobToStorage(bucket, thumbPath, thumbBlob);

      const smallBlob = await drawToWebP(img, SMALL_DIM, SMALL_QUALITY);
      const smallPath = `${folder}/${base}-small.webp`;
      await uploadBlobToStorage(bucket, smallPath, smallBlob);

      result.thumbPath = thumbPath;
      result.thumbUrl = publicUrl(bucket, thumbPath);
      result.smallPath = smallPath;
      result.smallUrl = publicUrl(bucket, smallPath);
    }

    onProgress?.("done", "✓ Imagen optimizada");
    return result;
  } catch (error) {
    onProgress?.("error", "No pudimos procesar esta imagen. Intenta nuevamente.");
    throw error instanceof Error
      ? error
      : new Error("No se pudo procesar la imagen.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Sube un blob ya procesado (p. ej. una foto con el ticket quemado) generando
 * también sus miniaturas. El archivo principal se sube tal cual para preservar
 * exactamente la marca de verificación.
 */
export async function uploadProcessedImage(opts: {
  blob: Blob;
  bucket: string;
  folder: string;
  kind?: ImageKind;
  prefix?: string;
}): Promise<ProcessedImage> {
  const { blob, bucket, folder, kind = "product", prefix = "imagen" } = opts;
  const cfg = KIND_CONFIG[kind];

  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const base = safeImageBase(prefix);
    const mainPath = `${folder}/${base}.webp`;
    await uploadBlobToStorage(bucket, mainPath, blob);

    const result: ProcessedImage = {
      path: mainPath,
      url: publicUrl(bucket, mainPath),
    };

    if (cfg.thumbnails) {
      const thumbBlob = await drawToWebP(img, THUMB_DIM, THUMB_QUALITY);
      const thumbPath = `${folder}/${base}-thumb.webp`;
      await uploadBlobToStorage(bucket, thumbPath, thumbBlob);

      const smallBlob = await drawToWebP(img, SMALL_DIM, SMALL_QUALITY);
      const smallPath = `${folder}/${base}-small.webp`;
      await uploadBlobToStorage(bucket, smallPath, smallBlob);

      result.thumbPath = thumbPath;
      result.thumbUrl = publicUrl(bucket, thumbPath);
      result.smallPath = smallPath;
      result.smallUrl = publicUrl(bucket, smallPath);
    }

    return result;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Elimina una imagen y sus miniaturas derivadas (si existen). Acepta una ruta
 * interna o una URL pública. Los archivos inexistentes se ignoran sin error.
 */
export async function removeStoredImage(
  bucket: string,
  pathOrUrl: string
): Promise<void> {
  if (!pathOrUrl) return;
  let path = pathOrUrl;
  const marker = `/object/public/${bucket}/`;
  const idx = pathOrUrl.indexOf(marker);
  if (idx >= 0) {
    path = decodeURIComponent(pathOrUrl.slice(idx + marker.length));
  }
  const base = path.replace(/\.webp$/i, "");
  await supabase.storage.from(bucket).remove([
    path,
    `${base}-thumb.webp`,
    `${base}-small.webp`,
  ]);
}